import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IUser, IUsers } from './usersType';
import { RootState, TThunkApi } from 'store/store';
import * as manApi from 'api/ManApi';
import { changeUserName } from 'store/thisUser/thisUserSlice';
import { isANumber } from '../../utils/number';
import {
  ICompany,
  ICompanyMember,
  ICompanyServer,
} from '../companies/companiesTypes';
import { ILimitation, updateLimitation } from '../limitations/limitationsSlice';

const initialState: IUsers = { map: {} };

function addUserHandler(state: IUsers, action: PayloadAction<{ user: IUser }>) {
  if (!state.map[action.payload.user.id]) {
    // @ts-expect-error it's updating step by step
    state.map = {
      ...state.map,
      [action.payload.user.id]: {},
    };
  }

  state.map[action.payload.user.id] = {
    ...action.payload.user,
    isLoading: false,
  };
}

function loadingUserHandler(
  state: IUsers,
  action: PayloadAction<{ userId: number }>
) {
  if (!state.map[action.payload.userId]) {
    // @ts-expect-error it's updating step by step
    state.map = {
      ...state.map,
      [action.payload.userId]: {},
    };
  }
  state.map[action.payload.userId] = {
    ...state.map[action.payload.userId],
    status: 'loading',
    isLoading: true,
  };
}

function setMultipleUserDataHandler(
  state: IUsers,
  action: PayloadAction<{ users: Array<IUser> }>
) {
  action.payload.users.forEach((user) => {
    state.map[user.id] = { ...user, isLoading: false };
  });
}

const limitationUpdate = createAsyncThunk(
  'users/limitationUpdate',
  async (
    {
      exceptedUsers,
      limitation,
    }: { exceptedUsers: Array<IUser>; limitation?: ILimitation },
    ThunkApi: TThunkApi
  ) => {
    ThunkApi.dispatch(limitationUpdateAction({ exceptedUsers }));
    ThunkApi.dispatch(updateLimitation({ limitation: limitation! }));
  }
);

function limitationUpdateHandler(
  state: IUsers,
  action: PayloadAction<{ exceptedUsers: Array<IUser> }>
) {
  if (!action.payload.exceptedUsers) return;

  action.payload.exceptedUsers.forEach((user) => {
    if (user.id) {
      state.map[user.id] = { ...user, isLoading: false };
    }
  });
}

//Thunks
export const requireUserFn = async (
  { userId }: { userId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  const user = store.users.map[userId];
  if (user) {
    return Promise.resolve({ user });
  }

  return manApi
    .user_get(userId, store)
    .then((res) => {
      ThunkApi.dispatch(addUser({ user: res.data }));
      return Promise.resolve({ user: res.data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const requireUser = createAsyncThunk('users/requireUser', requireUserFn);

export const updateUserFn = async (
  { user }: { user: IUser },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.userUpdate(store, user).then(
    (res) => {
      ThunkApi.dispatch(changeUserName(res.data.name));
      ThunkApi.dispatch(addUser({ user }));
      return Promise.resolve({ user });
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};

const updateUser = createAsyncThunk('users/updateUser', updateUserFn);

export const searchUserFn = async (
  { phoneOrEmail }: { phoneOrEmail: string },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .user_search(phoneOrEmail, store)
    .then(({ data: users }) => {
      ThunkApi.dispatch(setMultipleUserData({ users }));
      return Promise.resolve({ users });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const searchUser = createAsyncThunk('users/searchUser', searchUserFn);

export const requireSeveralUserFn = async (
  { usersList }: { usersList: Array<number> },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const usersId = Object.keys(store.users.map);
  const requiredUsersId = usersList.filter(
    (item) => usersId.indexOf(String(item)) < 0
  );

  if (requiredUsersId.length === 0) {
    return Promise.resolve(null);
  }

  return manApi
    .user_getAll(requiredUsersId, store)
    .then(({ data }) => {
      ThunkApi.dispatch(setMultipleUserData({ users: data }));
      return Promise.resolve({
        users: data,
      });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const requireSeveralUser = createAsyncThunk(
  'users/requireSeveralUser',
  requireSeveralUserFn
);

export const fetchAllUsersFn = async (
  { usersIds }: { usersIds: Array<number> },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .user_getAll(usersIds, store)
    .then(({ data }) => {
      if (data) {
        ThunkApi.dispatch(setMultipleUserData({ users: data }));
      }
      return Promise.resolve({ users: data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const fetchAllUsers = createAsyncThunk('users/fetchAllUsers', fetchAllUsersFn);

export const changeUserPhoneFn = async (
  {
    username,
    userId,
    currentPhone,
    newPhone,
    swapIfExists,
    newName,
  }: {
    username: string;
    userId: string;
    currentPhone: string;
    newPhone: string;
    swapIfExists: boolean;
    newName?: string;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .user_changePhone(
      username,
      userId,
      currentPhone,
      newPhone,
      swapIfExists,
      store,
      newName
    )
    .then(({ data: users }) => {
      if (!isANumber(Number(users))) {
        ThunkApi.dispatch(setMultipleUserData({ users }));
      }
      return Promise.resolve({ users });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const changeUserPhone = createAsyncThunk(
  'users/changeUserPhone',
  changeUserPhoneFn
);

export const requireCompanyUsersFn = async (_: any, ThunkApi: TThunkApi) => {
  // const companies = await ThunkApi.dispatch(
  //   getMyCompanies({ saveResults: false })
  // )
  //   .unwrap()
  //   .then((data) => data.companies);

  const store = ThunkApi.getState() as RootState;
  const companies = Object.values(store.companies.data);
  const usersList: Array<number> = [];
  companies.forEach((company: ICompany | ICompanyServer) => {
    const members = Array.isArray(company.members)
      ? company.members
      : Object.values(
          (company.members as { [key: number]: ICompanyMember }) || {}
        );
    members.forEach((member) => {
      usersList.push(member.userId);
    });
  });

  ThunkApi.dispatch(requireSeveralUser({ usersList }));
};

const requireCompanyUsers = createAsyncThunk(
  'users/requireCompanyUsers',
  requireCompanyUsersFn
);

const usersSlice = createSlice({
  name: 'users',
  initialState: initialState,
  reducers: {
    addUser: addUserHandler,
    loadingUser: loadingUserHandler,
    setMultipleUserData: setMultipleUserDataHandler,
    limitationUpdateAction: limitationUpdateHandler,
  },
});

export const {
  addUser,
  loadingUser,
  setMultipleUserData,
  limitationUpdateAction,
} = usersSlice.actions;

export {
  requireUser,
  updateUser,
  searchUser,
  requireSeveralUser,
  fetchAllUsers,
  changeUserPhone,
  requireCompanyUsers,
  limitationUpdate,
};

export default usersSlice.reducer;
